import {
  ColumnSort,
  Filters,
  SMsWithModels,
  TableDataWithImage,
  users,
} from '@spo/logic';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  PixlSupermodelsResponse,
  SpoPersonalOfferTableResponseDataItem,
} from '@spo/types';
import { format } from 'date-fns';
import genericImage from '../../../assets/genericImage.jpeg';
import { environment } from '../../../environments/environment';
import { useImagesBySmIds } from '../../../hooks/useImagesBySmIds';
import { useModels } from '../../../hooks/useModel';
import { useUserConfig } from '../../../hooks/useUserConfig';
import { columnsSuperModel } from '../../../utils/columns/columns';
import { SMTable } from '../SMTable';
import { ModelsTable } from '../modelsTable';

export interface TableContainerProps {
  superModelsTableData: SpoPersonalOfferTableResponseDataItem[];
  isFetchingSM: boolean;
  tabViewIndex: number;
  selectedFilters: Filters;
  columnSort: ColumnSort;
  setColumnSort: (sort: ColumnSort) => void;
  itemsPerPage: number;
}

export const TableContainer = ({
  superModelsTableData,
  isFetchingSM,
  tabViewIndex,
  columnSort,
  selectedFilters,
  setColumnSort,
}: TableContainerProps) => {
  const navigate = useNavigate();
  const contentMediaUrl = environment.contentMediaUrl;

  const { data: userConfigData, isFetching: isUserConfigFetching } =
    useUserConfig();

  const [sMsWithModels, setSMsWithModels] = useState<SMsWithModels[]>();
  const [sMsWithImage, setSMsWithImage] = useState<TableDataWithImage[]>();

  const smIds = superModelsTableData.map((sm) => sm.id);

  const dateFormatted =
    userConfigData &&
    userConfigData.offerDate &&
    format(Date.parse(userConfigData.offerDate), 'dd MMMM yyyy');

  const params = new URLSearchParams(document.location.search);
  const paramSmIds = params.get('smIds')?.split(',') || [];
  const paramModelIds = params.get('modelIds')?.split(',') || [];

  const {
    data: modelData,
    isError: modelIsError,
    isFetching: isModelsFetching,
  } = useModels({ smIds, modelIds: [] }, userConfigData?.offerDate);

  const { data: imagesDataBySmIds } = useImagesBySmIds({
    smIds,
    offerDate: userConfigData?.offerDate,
    modelData,
    width: 120,
    height: 120,
    contentMediaUrl,
  });

  useEffect(() => {
    if (userConfigData && !isUserConfigFetching) {
      if (userConfigData.needRedirect) {
        navigate('/personal-offer/my-config');
      } else {
        const urlWithParameters = users.buildUrlFromUserConfiguration(
          userConfigData,
          '/personal-offer/my-view',
        );
        navigate(urlWithParameters, { replace: true });
      }
    }
  }, [navigate, userConfigData, isUserConfigFetching]);

  useEffect(() => {
    const modelAccordionContainer = document.getElementById(
      'model-accordion-container',
    );
    if (modelAccordionContainer) {
      modelAccordionContainer.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [modelData]);

  useEffect(() => {
    if (modelData) {
      const sMWithModel = superModelsTableData.map((sm) => ({
        smId: sm.id,
        description: sm.descriptionEn,
        models: modelData
          .filter((model) => model.superModelId === sm.id)
          .filter((model) => model.status !== 'CANCELLED'),
      }));
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSMsWithModels(sMWithModel);
    }
  }, [superModelsTableData, modelData]);

  const getImageOrGeneric = (
    sm: SpoPersonalOfferTableResponseDataItem,
    imagesDataBySmIds: PixlSupermodelsResponse | undefined,
  ) => {
    if (imagesDataBySmIds) {
      return (
        imagesDataBySmIds.find((img) => img.smId === Number(sm.id))?.imageURL ||
        genericImage
      );
    } else {
      return genericImage;
    }
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSMsWithImage(
      superModelsTableData.map((sm) => ({
        ...sm,
        imageURL: getImageOrGeneric(sm, imagesDataBySmIds),
      })),
    );
  }, [imagesDataBySmIds, superModelsTableData]);

  return tabViewIndex === 0 ? (
    <SMTable
      data={sMsWithImage || []}
      columnSort={columnSort}
      setColumnSort={setColumnSort}
      columns={columnsSuperModel()}
      isLoading={isFetchingSM}
      defaultSMIds={paramSmIds}
    />
  ) : (
    <ModelsTable
      sMsWithModels={sMsWithModels}
      modelIsError={modelIsError}
      selectedFilters={selectedFilters}
      dateFormatted={dateFormatted}
      isLoading={isModelsFetching}
      defaultModelIds={paramModelIds}
    />
  );
};
